
import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

export interface SubscriptionData {
  id: string;
  plan: string;
  status: string;
  nextBillingDate: string;
  amount: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
}

export const useSubscriptionData = () => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchSubscription = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // First, try to get any active subscription
      let { data: activeSubscriptions, error: activeError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('updated_at', { ascending: false })
        .limit(1);
        
      if (activeError) {
        throw activeError;
      }
      
      // If no active subscription, get the most recent subscription of any status
      if (!activeSubscriptions || activeSubscriptions.length === 0) {
        const { data: anySubscriptions, error: anyError } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })
          .limit(1);
          
        if (anyError) {
          throw anyError;
        }
        
        activeSubscriptions = anySubscriptions;
      }
      
      // If still no subscription found, return null
      if (!activeSubscriptions || activeSubscriptions.length === 0) {
        setSubscription(null);
        setIsLoading(false);
        return;
      }
      
      // Use the first subscription in the array (most recently updated)
      const data = activeSubscriptions[0];
      
      const planDisplay = data.plan ? 
        (data.plan === 'annual' ? 'Annual' : 
         data.plan === 'lifetime' ? 'Lifetime' : 
         'Monthly') : 
        'Pro Monthly';
        
      const nextBillingDate = data.updated_at ? 
        new Date(new Date(data.updated_at).getTime() + 30 * 24 * 60 * 60 * 1000) : 
        new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000);
        
      setSubscription({
        id: data.id,
        plan: planDisplay,
        status: data.status || 'active',
        nextBillingDate: format(nextBillingDate, 'MMMM d, yyyy'),
        amount: data.plan === 'annual' ? '$79 / year' : 
               data.plan === 'lifetime' ? '$199 one-time' : 
               '$19 / month',
        stripeCustomerId: data.stripe_customer_id,
        stripeSubscriptionId: data.stripe_subscription_id
      });
      
    } catch (err: any) {
      console.error('Error fetching subscription:', err);
      setError(err.message);
      toast({
        title: 'Error loading subscription data',
        description: err.message,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchSubscription();
  }, []);
  
  return {
    subscription,
    isLoading,
    error,
    refreshSubscription: fetchSubscription
  };
};
